import type { AddDelayType } from '@library/types/boardBuilder';
import { AdjustDelayPopover } from '@library/components/boardBuilder';
import { Button } from '@library/components/buttons';
import { DateTime } from 'luxon';
import type { LeagueGameData } from '@library/types/games';
import chevronDownIcon from '@library/assets/images/icons/chevron-down-icon.svg';
import { classNames } from '@library/utils';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getTeamName } from '@bo-utils/trading';
import { toTitleCase } from '@bo-utils/common';
import { useState } from 'react';

export interface BoardBuilderGameHeaderProps {
  onTogglePlayers: () => void;
  onAddDelay: ({ gameId, delay }: AddDelayType) => void;
  game: LeagueGameData;
  showPlayers: boolean;
}

export const BoardBuilderGameHeader = ({
  onTogglePlayers,
  onAddDelay,
  game,
  showPlayers
}: BoardBuilderGameHeaderProps) => {
  const {
    away_team: awayTeam,
    away_team_market: awayMarket,
    away_team_name: awayName,
    date_time: startTime,
    display,
    home_team: homeTeam,
    home_team_market: homeMarket,
    home_team_name: homeName
  } = game;

  const timeZoneDateTime = formatWithTimezoneChange(
    DateTime.fromISO(startTime),
    'ccc t ZZZZ'
  );

  const [updatedStartTime, setUpdatedStartTime] = useState(
    DateTime.fromISO(startTime)
  );
  const handleUpdateStartTime = (newStartTime: DateTime) => {
    setUpdatedStartTime(newStartTime);
  };

  const handleSaveDelay = (delay: number, isTimePickerChange: boolean) => {
    onAddDelay({ gameId: game.game_id, delay, isTimePickerChange });
  };

  const awayTeamName = getTeamName({
    team: awayTeam,
    teamName: awayName,
    teamMarket: awayMarket
  });

  const homeTeamName = getTeamName({
    team: homeTeam,
    teamName: homeName,
    teamMarket: homeMarket
  });

  const getLiveProvidersDisplay = () => {
    if (!game.live_providers || game.live_providers.length === 0) {
      return '';
    }
    return ` (${game.live_providers
      .map((liveProvider) => toTitleCase(liveProvider, ', '))
      .join(', ')})`;
  };

  const liveProviders = getLiveProvidersDisplay();

  const gameTitle =
    awayTeamName && homeTeamName
      ? `${awayTeamName} vs. ${homeTeamName}${liveProviders}`
      : display;

  return (
    <div className="bg-soFresh-120 border-soFresh-140 box-border flex h-[72px] w-full flex-row items-center justify-between px-4 py-3">
      <div className="flex flex-none grow flex-col items-start gap-3 px-0 py-3">
        <div className="flex h-4 max-w-[546px] grow-0 flex-col items-center p-0">
          <div className="font-heebo text-soClean-160 order-none h-[9px] max-w-[426px] flex-none grow-0 text-[13px] font-normal leading-[19px] tracking-[0.006em]">
            {timeZoneDateTime}
          </div>
        </div>
        <div className="font-heebo text-soClean-100 order-none w-[305px] flex-none grow-0 overflow-hidden font-semibold leading-[19px] tracking-[0.006em]">
          {gameTitle}
        </div>
      </div>
      <div className="flex flex-none grow-0 flex-row items-start gap-0 p-0">
        <AdjustDelayPopover
          initialStartTime={DateTime.fromISO(startTime)}
          updatedStartTime={updatedStartTime}
          onUpdateStartTime={handleUpdateStartTime}
          onSaveDelay={handleSaveDelay}
        />
        <Button
          type="button"
          title="ToggleGameTeamPlayersButton"
          onClick={onTogglePlayers}
        >
          <img
            alt="Chevron Down icon"
            className={classNames(showPlayers ? 'rotate-180' : '')}
            src={chevronDownIcon}
          />
        </Button>
      </div>
    </div>
  );
};
